<template>
  <div class="mt-4 space-y-4" id="contentUsersActivityReport">
    <div class="rounded-lg border bg-white p-4">
      <div class="flex flex-wrap items-end gap-4">
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Fec. de inicio
          </span>
          <input
            v-model="filters.dateStart"
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            @keydown.enter.prevent
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Fec. término
          </span>
          <input
            v-model="filters.dateEnd"
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            @keydown.enter.prevent
          />
        </label>
        <label class="block flex-1 min-w-[280px]">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Buscar por usuario, pantalla o acción
          </span>
          <input
            v-model="filters.search"
            type="text"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            placeholder="Ej: ventas, reporte, usuario, cierre"
            @keydown.enter.prevent="generateReport"
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Módulo
          </span>
          <input
            v-model="filters.module"
            type="text"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            placeholder="ventas, reportes, caja"
            @keydown.enter.prevent="generateReport"
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Resultado
          </span>
          <select
            v-model="filters.statusScope"
            class="border-2 bg-gray-50 py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[150px] font-semibold w-full focus:border-blue-500 uppercase"
          >
            <option value="ALL">todos</option>
            <option value="SUCCESS">exitosos</option>
            <option value="ERROR">con errores</option>
          </select>
        </label>
        <button
          class="bg-blue-500 text-white hover:bg-blue-600 border-2 border-blue-500 py-2 rounded-md active:scale-95 px-3 text-sm w-full md:w-auto"
          @click="generateReport"
        >
          <font-awesome-icon icon="fa-solid fa-search" class="rotate-90 mr-1" />
          Buscar
        </button>
        <button
          class="bg-slate-100 text-slate-700 hover:bg-slate-200 border-2 border-slate-200 py-2 rounded-md active:scale-95 px-3 text-sm w-full md:w-auto"
          @click="clearFilters"
        >
          Limpiar
        </button>
      </div>
    </div>

    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <article
        v-for="card in summaryCards"
        :key="card.title"
        class="rounded-xl border bg-white p-4 shadow-sm"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {{ card.title }}
        </p>
        <h3 class="mt-2 text-2xl font-bold text-slate-700">{{ card.value }}</h3>
        <p class="mt-1 text-sm text-slate-500">{{ card.description }}</p>
      </article>
    </div>

    <div class="rounded-lg overflow-hidden border bg-white">
      <div class="py-4 px-3 flex items-center justify-between gap-4 flex-wrap">
        <select
          v-model="limitRegister"
          class="bg-white border py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[140px] font-semibold w-full md:w-auto"
          @change="resetValuesAndGetData"
        >
          <option value="10">10 Registros</option>
          <option value="25">25 Registros</option>
          <option value="50">50 Registros</option>
          <option value="100">100 Registros</option>
        </select>
        <button
          class="bg-orange-500 text-white hover:bg-orange-600 border-2 border-orange-500 py-2 rounded-md active:scale-95 px-3 text-sm w-full md:w-auto"
          @click="downloadPDF"
          :disabled="loading"
        >
          <font-awesome-icon icon="fa-solid fa-print" class="mr-1" />
          Exportar PDF
        </button>
      </div>

      <div class="w-full resize-x resize-generate" ref="widthNav" @resize="handleResize">
        <div class="overflow-x-auto">
          <table class="table-auto w-full" id="usersActivityTable">
            <TableHead :headers="header" />
            <LoadingTables v-if="loading" :columns="8" />
            <tbody class="text-gray-500 text-left border-t-[1px]" v-else>
              <tr
                v-for="register in registers"
                :key="register.id"
                class="hover:bg-blue-500 transition-colors hover:text-white font-semibold odd:bg-white even:bg-slate-50"
                @click="selectRow($event)"
              >
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ register.usuario }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[220px]">
                  {{ register.pantalla || 'Sin pantalla' }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[140px]">
                  {{ register.modulo || 'Sin módulo' }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[220px]">
                  {{ register.ruta_cliente || 'Sin ruta cliente' }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[220px]">
                  {{ register.accion }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap text-right">
                  {{ format(register.total_eventos) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap text-right">
                  {{ format(register.total_errores) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap text-right">
                  {{ formatDateTime(register.ultima_actividad_at) }}
                </td>
              </tr>
              <tr v-if="!registers.length && !loading">
                <td colspan="8" class="px-6 py-8 text-center text-slate-400">
                  No encontramos actividad con los filtros actuales.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        :currentPage="currentPage"
        :total="totalPage"
        :next="next"
        :prev="prev"
      />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from "vue";
import axios from "@/api";
import LoadingTables from "@/components/Loadings/LoadingTables";
import TableHead from "@/components/TablesComponents/TableHead";
import TablePagination from "@/components/TablesComponents/TablePagination.vue";
import { errorActions, warningActions } from "@/alerts";
import { idLocalStore } from "@/publicjs/localStorage";
import { firstDaytAndLastDay } from "@/publicjs/getFirstAndLastDayOfMount";
import { formatDateTime } from "@/publicjs/formatDate";
import { format } from "@/publicjs/money";
import { selectRow } from "@/publicjs/selectRowTable";
import { observeElement } from "@/observer";
import { exportToPDF } from "./Reports/PDFExportUsersActivity";

export default {
  name: "MainUsersActivityReport",
  components: {
    LoadingTables,
    TableHead,
    TablePagination,
  },
  setup() {
    const loading = ref(true);
    const idStore = ref(0);
    const currentPage = ref(1);
    const totalPage = ref(1);
    const limitRegister = ref(10);
    const registers = ref([]);
    const summary = ref({
      total_events: 0,
      total_errors: 0,
      active_users: 0,
      unique_steps: 0,
      last_activity_at: null,
    });

    const header = [
      [
        { title: "usuario" },
        { title: "pantalla" },
        { title: "módulo" },
        { title: "ruta" },
        { title: "acción" },
        { title: "eventos" },
        { title: "errores" },
        { title: "última actividad" },
      ],
    ];

    const filters = ref({
      dateStart: null,
      dateEnd: null,
      search: "",
      module: "",
      statusScope: "ALL",
    });

    const summaryCards = computed(() => [
      {
        title: "Eventos acumulados",
        value: format(summary.value.total_events),
        description: "Actividad resumida registrada en el período",
      },
      {
        title: "Usuarios activos",
        value: format(summary.value.active_users),
        description: "Usuarios con movimiento dentro del filtro",
      },
      {
        title: "Pasos únicos",
        value: format(summary.value.unique_steps),
        description: "Pantallas o rutas distintas recorridas",
      },
      {
        title: "Errores detectados",
        value: format(summary.value.total_errors),
        description: summary.value.last_activity_at
          ? `Último movimiento: ${formatDateTime(summary.value.last_activity_at)}`
          : "Sin actividad reciente",
      },
    ]);

    const getData = async () => {
      loading.value = true;

      const response = await axios
        .get(`actividad-de-usuarios/${idStore.value}`, {
          params: {
            date_start: filters.value.dateStart,
            date_end: filters.value.dateEnd,
            search: filters.value.search,
            module: filters.value.module,
            status_scope: filters.value.statusScope,
            page: currentPage.value,
            per_page: limitRegister.value,
          },
        })
        .catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      summary.value = response.data.summary || summary.value;
      registers.value = response.data.details?.data || [];
      currentPage.value = response.data.details?.current_page || 1;
      totalPage.value = response.data.details?.last_page || 1;

      if (!registers.value.length && !summary.value.total_events) {
        warningActions("No encontramos actividad registrada con ese filtro");
      }

      loading.value = false;
    };

    const generateReport = async () => {
      currentPage.value = 1;
      await getData();
    };

    const resetValuesAndGetData = async () => {
      currentPage.value = 1;
      await getData();
    };

    const clearFilters = async () => {
      const dates = await firstDaytAndLastDay();
      filters.value.dateStart = dates.firstDay;
      filters.value.dateEnd = dates.lastDay;
      filters.value.search = "";
      filters.value.module = "";
      filters.value.statusScope = "ALL";
      await generateReport();
    };

    const downloadPDF = async () => {
      const response = await axios
        .get(`actividad-de-usuarios/${idStore.value}`, {
          params: {
            date_start: filters.value.dateStart,
            date_end: filters.value.dateEnd,
            search: filters.value.search,
            module: filters.value.module,
            status_scope: filters.value.statusScope,
            page: 1,
            per_page: 1000,
          },
        })
        .catch((error) => errorActions(error));

      if (!response) {
        return;
      }

      const pdfRegisters = response.data.details?.data || [];
      if (!pdfRegisters.length) {
        warningActions("No hay datos para exportar en PDF con esos filtros");
        return;
      }

      await exportToPDF(pdfRegisters, response.data.summary, filters.value);
    };

    const next = async () => {
      if (currentPage.value < totalPage.value) {
        currentPage.value++;
        await getData();
      }
    };

    const prev = async () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        await getData();
      }
    };

    const widthNav = ref(null);
    const handleResize = () => {
      const nav = document.querySelector("nav")?.offsetWidth || 0;
      watchEffect(() => {
        if (widthNav.value) {
          widthNav.value.style.width = `calc(100vw - ${nav + 42}px)`;
        }
      });
    };

    onMounted(async () => {
      observeElement("#contentUsersActivityReport");
      observeElement("#usersActivityTable");
      handleResize();
      window.addEventListener("resize", handleResize);
      idStore.value = await idLocalStore();
      const dates = await firstDaytAndLastDay();
      filters.value.dateStart = dates.firstDay;
      filters.value.dateEnd = dates.lastDay;
      await getData();
    });

    return {
      loading,
      filters,
      registers,
      summary,
      summaryCards,
      header,
      currentPage,
      totalPage,
      limitRegister,
      generateReport,
      resetValuesAndGetData,
      clearFilters,
      downloadPDF,
      next,
      prev,
      formatDateTime,
      format,
      selectRow,
      widthNav,
      handleResize,
    };
  },
};
</script>

<style scoped>
.active button svg {
  rotate: 90deg;
}
</style>